import React, { useMemo } from 'react';

// MUI Components
import DataGrid from '~/components/General/OlcDataGrid';
import {
  Pagination,
  Typography,
  Box,
  PaginationItem,
  useMediaQuery,
  Select,
  MenuItem,
} from '@mui/material';

// Components

// Icons
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { GeneralPencilCIcon } from '~/assets/images';

// Utils
import { DESIGNER_QUERY_PROOF_PREVIEW_STATUSES } from '~/utils/constants';

// Styles
import '~/components/Templates/TemplateListing/styles.scss';

const PAGE_SIZE_OPTIONS = [5, 10, 15, 20];

const isProofPreviewableStatus = (status = '') =>
  DESIGNER_QUERY_PROOF_PREVIEW_STATUSES.includes(status);

const HistoryListing = ({
  rows = [],
  loading = false,
  pagination = {},
  onPageChange,
  onPerPageChange,
  onReviewProof,
}) => {
  const isWideScreen = useMediaQuery('(min-width:1200px)');
  const getColumnFlex = (value) => (isWideScreen ? value : 0);
  const rowsCount = pagination?.total || pagination?.count || rows.length;
  const from = pagination?.from || 0;
  const to = pagination?.to || 0;
  const currentPage = pagination?.currentPage || 1;
  const perPage = pagination?.perPage || 5;
  const pageCount = Math.max(pagination?.lastPage || 1, 1);

  const handleChangePage = (event, newPage) => {
    onPageChange?.(newPage);
  };

  const handleChangePerPage = (pageSize) => {
    onPerPageChange?.(pageSize);
  };

  const columns = useMemo(
    () => [
      {
        field: 'templateName',
        headerName: 'Template Name',
        width: 230,
        flex: getColumnFlex(1),
        sortable: false,
        renderCell: (params) => (
          <Box className="tempValueWrapper">
            <Typography>
              {params.row.templateName}
              {params.row.raw?.sourceTemplateId ? (
                <img className="editRequestIcon" src={GeneralPencilCIcon} alt="edit request" />
              ) : null}
            </Typography>
            {params.row.templateId ? <Typography>ID: {params.row.templateId}</Typography> : null}
          </Box>
        ),
      },
      {
        field: 'date',
        headerName: 'Date',
        width: 160,
        flex: getColumnFlex(0.75),
        sortable: false,
        renderCell: (params) => (
          <Box className="addressWrapper">
            <Typography>{params.row.date}</Typography>
          </Box>
        ),
      },
      {
        field: 'productType',
        headerName: 'Product Type',
        width: 200,
        flex: getColumnFlex(1),
        sortable: false,
        renderCell: (params) => (
          <Box className="addressWrapper productTypeCell">
            <Typography>{params.row.productType || '-'}</Typography>
          </Box>
        ),
      },
      {
        field: 'submittedDate',
        headerName: 'Submitted Date',
        width: 220,
        flex: getColumnFlex(1.05),
        sortable: false,
        renderCell: (params) => (
          <Box className="addressWrapper">
            <Typography>{params.row.submittedDate}</Typography>
          </Box>
        ),
      },
      {
        field: 'submittedBy',
        headerName: 'Submitted By',
        width: 180,
        flex: getColumnFlex(0.8),
        sortable: false,
        renderCell: (params) => (
          <Box className="addressWrapper">
            <Typography>{params.row.submittedBy}</Typography>
          </Box>
        ),
      },
      {
        field: 'status',
        headerName: 'Status',
        width: 230,
        flex: getColumnFlex(1),
        sortable: false,
        renderCell: (params) => (
          <Box className="addressWrapper statusCell">
            <Typography
              className={isProofPreviewableStatus(params.row.status) ? 'proofStatusLink' : ''}
              onClick={(event) => {
                if (!isProofPreviewableStatus(params.row.status)) {
                  return;
                }

                event.stopPropagation();
                onReviewProof?.(params.row);
              }}
            >
              {params.row.status}
            </Typography>
          </Box>
        ),
      },
      {
        field: 'completedDate',
        headerName: 'Completed Date',
        width: 220,
        flex: getColumnFlex(0.9),
        sortable: false,
        renderCell: (params) => (
          <Box className="addressWrapper">
            <Typography>{params.row.completedDate}</Typography>
          </Box>
        ),
      },
    ],
    [isWideScreen, onReviewProof]
  );

  const classes = {
    root: 'tableGrid',
    main: 'tableGridMain',
    columnHeaders: 'tableGridHeaderWrapper',
    columnHeader: 'tableGridColumnHeader',
    columnHeaderTitle: 'tableGridColumnHeaderTitle',
    virtualScrollerContent: 'tableGridVirtualScrollerContent',
    virtualScrollerRenderZone: 'tableGridVirtualScrollerRenderZone',
    cell: 'tableGridCell',
    row: 'tableGridRow',
    columnHeaderTitleContainer: 'tableGridColumnHeaderTitleContainer',
  };

  return (
    <Box className="hireDesignerHistory">
      <Typography className="historyTitle">History</Typography>
      <Box
        style={{
          marginTop: '19px',
          width: '100%',
        }}
        className={`templateListingWrapper historyListingWrapper ${rows.length === 0 ? 'noResultsTable' : ''}`}
      >
        <DataGrid
          autoHeight
          rows={rows}
          columns={columns}
          loading={loading}
          pageSizeOptions={PAGE_SIZE_OPTIONS}
          rowCount={rowsCount}
          paginationMode="server"
          hideFooterSelectedRowCount
          hideFooterPagination
          rowSelection={false}
          getRowId={(row) => row?.id}
          rowHeight={59}
          headerHeight={47}
          disableColumnMenu
          getRowClassName={() => 'tableGridRow'}
          classes={{
            ...classes,
            virtualScroller: `tableGridVirtualScroller ${rows.length === 0 ? 'noResults' : ''}`,
          }}
          slots={{
            noRowsOverlay: () => (
              <Typography className="noResultsText">No Designs to Show</Typography>
            ),
            noResultsOverlay: () => (
              <Typography className="noResultsText">No Designs to Show</Typography>
            ),
          }}
        />
        {rowsCount > 0 && (
          <Box className="generalPaginationWrapper historyListingPaginationWrapper">
            <Pagination
              count={pageCount}
              variant="outlined"
              shape="rounded"
              onChange={handleChangePage}
              page={currentPage}
              renderItem={(item) => (
                <PaginationItem
                  slots={{
                    previous: ArrowBackIosNewIcon,
                    next: ArrowForwardIosIcon,
                  }}
                  {...item}
                />
              )}
            />
            <Select
              className={perPage >= 100 ? 'pageSelect pageSelectChange' : 'pageSelect'}
              inputProps={{ 'aria-label': 'Results per page' }}
              value={perPage}
            >
              {PAGE_SIZE_OPTIONS.map((pageSize) => {
                return (
                  <MenuItem
                    key={pageSize}
                    value={pageSize}
                    onClick={() => {
                      handleChangePerPage(pageSize);
                    }}
                  >
                    {pageSize}
                  </MenuItem>
                );
              })}
            </Select>
            <Typography>
              Showing {from} to {to} of {rowsCount} results
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default HistoryListing;
